// Pure helpers for the New Stock Transfer page's product search + "Select
// product" dropdown.
//
// GitHub issue #57 — the product search field and dropdown never showed any
// products. The page gated the products fetch on a chosen *source location*
// (`enabled: Boolean(sourceLocationId)`), but the admin shell defaults to
// "all locations" so `sourceLocationId` starts empty. With the fetch skipped,
// the search box and dropdown stayed permanently empty. This is the same root
// cause as issue #73 (Reservations table dropdown): keep the location logic in
// one testable place and always fetch, scoping by location only when one is
// actually selected.

/**
 * Query params for the product list shown in the transfer item dropdown.
 *
 * `sourceLocationId` is passed through only when it is a non-empty string, so
 * before a source location is picked the API returns every active product for
 * the tenant (instead of nothing) and the dropdown always has options. Once a
 * source location is chosen the list is scoped to that location's stock.
 *
 * A variant parent is a grouping placeholder, not a stockable item: its units
 * live on its children and the POS can only sell a specific variant, so moving
 * a parent SKU would strand the stock on a row nothing can sell at the
 * destination either. The dropdown therefore lists the catalog's leaves —
 * standalone products plus variant children — by asking for variants
 * (`include_variants`) while excluding rows that have children of their own
 * (`has_variants: 'false'`). Without `include_variants` the list defaults to
 * top-level rows only, which would drop every variant and leave the unusable
 * parents as the only choice.
 */
export function buildProductSearchParams(
  productSearch: string,
  sourceLocationId: string,
): {
  search?: string;
  location_id?: string;
  limit: number;
  is_active: string;
  include_variants: string;
  has_variants: string;
} {
  return {
    search: productSearch.trim() || undefined,
    location_id: sourceLocationId || undefined,
    limit: 50,
    is_active: 'true',
    include_variants: 'true',
    has_variants: 'false',
  };
}
